// Sprint 5 · Paso 3 · Modal de evidencia con carga diferida.
// Se abre solo al hacer click en el icono de una evidencia. Nunca carga el
// contenido hasta ese momento.
import React, { useEffect, useState } from 'react';
import { X, FileText, Image as ImageIcon, Mic, Loader2, Info } from 'lucide-react';
import { casosApi } from '../../../lib/dashboardApi';
import { track } from '../../../lib/metrics';

const ICON = { texto: FileText, foto: ImageIcon, audio: Mic };

export default function EvidenciaModal({ evidenciaId, tipo, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!evidenciaId) return;
    let ignore = false;
    setLoading(true); setError('');
    track('evidencia_open', { tipo: tipo || 'na' });
    casosApi
      .evidencia(evidenciaId)
      .then((d) => { if (!ignore) setData(d); })
      .catch((e) => { if (!ignore) setError(e.message || 'No se pudo cargar la evidencia.'); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [evidenciaId]);

  const Icon = ICON[tipo] || FileText;
  const label = tipo === 'texto' ? 'Nota / Relato' : tipo === 'foto' ? 'Imagen' : tipo === 'audio' ? 'Audio' : 'Evidencia';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="evidencia-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl border border-[#E5EAF5] p-5 max-w-2xl w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="evidencia-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#5346E6]" />
            <h3 className="font-extrabold text-[#0B1B5E] text-sm">{label}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F4F7FE]" data-testid="evidencia-modal-close">
            <X className="w-4 h-4 text-[#475472]" />
          </button>
        </div>

        {loading && (
          <div className="py-8 text-center text-sm text-[#475472]" data-testid="evidencia-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando evidencia…
          </div>
        )}
        {error && (
          <div className="py-4 text-sm text-[#8A1616] bg-[#FADBD8] rounded-lg px-3" data-testid="evidencia-error">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.tipo === 'texto' && (
              <p className="text-sm text-[#0B1B5E] whitespace-pre-wrap" data-testid="evidencia-texto">
                {data.contenido_texto || <span className="text-[#94A3B8] italic">Sin contenido registrado.</span>}
              </p>
            )}
            {(data.tipo === 'foto' || data.tipo === 'audio') && (
              <div>
                {data.is_synthetic ? (
                  <div className="flex items-start gap-2 text-xs text-[#7A4A0B] bg-[#FFF1D6] border border-[#F6B73C] rounded-lg p-3" data-testid="evidencia-sintetica">
                    <Info className="w-4 h-4 shrink-0" />
                    <div>
                      <p className="font-bold">Evidencia de demostración</p>
                      <p className="mt-1">
                        Este es un {data.tipo === 'foto' ? 'archivo de imagen' : 'audio'} sintético del entorno piloto.
                        En producción real aquí se mostraría el archivo original firmado desde Supabase Storage.
                      </p>
                      {data.segundos && <p className="mt-1">Duración simulada: {data.segundos} s</p>}
                      <p className="mt-1 text-[10px] font-mono text-[#7A4A0B]/70 break-all">{data.storage_path}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-[#475472]" data-testid="evidencia-real">
                    <p>Archivo: <code className="text-[11px] font-mono">{data.storage_path}</code></p>
                    {data.mime && <p className="text-xs mt-1">Tipo: {data.mime}</p>}
                    {data.segundos && <p className="text-xs mt-1">Duración: {data.segundos} s</p>}
                    <p className="mt-2 text-xs text-[#94A3B8]">La visualización directa del archivo se habilitará cuando se activen las URLs firmadas de Supabase Storage.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
